// Comprehensive Visa-on-Arrival (VOA) data for countries worldwide
// Updated January 2025 with accurate costs, requirements, and eligibility

export interface VisaOnArrivalInfo {
  country: string;
  available: boolean;
  eligiblePassports?: string[]; // Countries that can get VOA
  cost?: {
    amount: number;
    currency: string;
  };
  maxStayDays: number;
  airports?: string[]; // Airports/borders that offer VOA
  landBorders?: string[]; // Land borders that offer VOA
  requirements: {
    passportValidity: string; // e.g., "6 months beyond stay"
    photos: number; // Number of passport photos required
    returnTicket: boolean;
    hotelBooking: boolean;
    minimumFunds?: string;
    acceptedPaymentMethods?: string[]; // e.g., ["cash USD", "cash local currency", "card"]
  };
  notes?: string;
  lastUpdated: string; // Date of last verification
}

export const VISA_ON_ARRIVAL_DATA: Record<string, VisaOnArrivalInfo> = {
  // ============= SOUTHEAST ASIA =============
  'Thailand': {
    country: 'Thailand',
    available: true,
    eligiblePassports: [
      'India', 'China', 'Taiwan', 'Kazakhstan', 'Uzbekistan', 'Saudi Arabia',
      'Bulgaria', 'Bhutan', 'Ethiopia', 'Fiji', 'Georgia', 'Papua New Guinea',
      'Malta', 'Mexico', 'Nauru', 'Romania', 'Cyprus', 'Ukraine'
    ],
    cost: { amount: 2000, currency: 'THB' }, // ~$57 USD
    maxStayDays: 15,
    airports: [
      'Suvarnabhumi Airport (BKK)',
      'Don Mueang Airport (DMK)',
      'Chiang Mai Airport (CNX)',
      'Phuket Airport (HKT)',
      'Hat Yai Airport (HDY)',
      'U-Tapao Airport (UTP)',
      'Krabi Airport (KBV)'
    ],
    landBorders: ['Mae Sai', 'Nong Khai', 'Sadao', 'Padang Besar', 'Aranyaprathet'],
    requirements: {
      passportValidity: '6 months',
      photos: 1,
      returnTicket: true,
      hotelBooking: true,
      minimumFunds: '10,000 THB per person or 20,000 THB per family',
      acceptedPaymentMethods: ['cash THB', 'cash USD', 'card']
    },
    notes: 'VOA can be extended once for up to 30 days at immigration offices. Many nationalities get 30-60 days visa-free.',
    lastUpdated: '2025-01-09'
  },
  
  'Cambodia': {
    country: 'Cambodia',
    available: true,
    eligiblePassports: ['All nationalities except Afghanistan, Algeria, Bangladesh, Iran, Iraq, Pakistan, Saudi Arabia, Sri Lanka, Sudan'],
    cost: { amount: 30, currency: 'USD' },
    maxStayDays: 30,
    airports: [
      'Phnom Penh International Airport (PNH)',
      'Siem Reap Angkor International Airport (SAI)',
      'Sihanoukville International Airport (KOS)'
    ],
    landBorders: [
      'Poipet (Thai border)',
      'Bavet (Vietnam border)',
      'Cham Yeam (Thai border)',
      'Koh Kong (Thai border)',
      'O Smach (Thai border)'
    ],
    requirements: {
      passportValidity: '6 months',
      photos: 1,
      returnTicket: false,
      hotelBooking: false,
      acceptedPaymentMethods: ['cash USD']
    },
    notes: 'Tourist visa extendable for up to 30 days. E-visa also available at $36 USD. Photo can be taken on-site for $2.',
    lastUpdated: '2025-01-09'
  },

  'Laos': {
    country: 'Laos',
    available: true,
    eligiblePassports: ['All nationalities except Afghanistan, Bangladesh, Jordan, Palestine, Pakistan, Syria'],
    cost: { amount: 40, currency: 'USD' }, // Varies by nationality: $30-42
    maxStayDays: 30,
    airports: [
      'Wattay International Airport (VTE) - Vientiane',
      'Luang Prabang International Airport (LPQ)',
      'Pakse International Airport (PKZ)',
      'Savannakhet Airport (ZVK)'
    ],
    landBorders: [
      'Friendship Bridge (Thai border)',
      'Boten (Chinese border)',
      'Dansavanh (Vietnam border)',
      'Huay Xai (Thai border)',
      'Nong Khai (Thai border)'
    ],
    requirements: {
      passportValidity: '6 months',
      photos: 2,
      returnTicket: false,
      hotelBooking: false,
      acceptedPaymentMethods: ['cash USD', 'cash THB']
    },
    notes: 'Fee varies by nationality: US/Canada $42, UK/EU $35, others $30-40. Additional $1-2 overtime fee on weekends/holidays.',
    lastUpdated: '2025-01-09'
  },

  'Indonesia': {
    country: 'Indonesia',
    available: true,
    eligiblePassports: [
      'All nationalities eligible for VOA (95+ countries)',
      'Includes USA, UK, EU, Australia, Japan, South Korea, China, India'
    ],
    cost: { amount: 500000, currency: 'IDR' }, // ~$32 USD
    maxStayDays: 30,
    airports: [
      'Soekarno-Hatta Airport (CGK) - Jakarta',
      'Ngurah Rai Airport (DPS) - Bali',
      'Juanda Airport (SUB) - Surabaya',
      'Kualanamu Airport (KNO) - Medan',
      'Yogyakarta Airport (YIA)',
      'Lombok Airport (LOP)'
    ],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: false,
      acceptedPaymentMethods: ['cash IDR', 'cash USD', 'card']
    },
    notes: 'Can be extended once for 30 days at immigration office (~$30). 169 countries get visa-free for 30 days. VOA allows extensions, visa-free does not.',
    lastUpdated: '2025-01-09'
  },

  'Vietnam': {
    country: 'Vietnam',
    available: true,
    eligiblePassports: ['Most nationalities via e-Visa or VOA with pre-approval letter'],
    cost: { amount: 25, currency: 'USD' }, // Stamping fee with approval letter
    maxStayDays: 30,
    airports: [
      'Tan Son Nhat Airport (SGN) - Ho Chi Minh City',
      'Noi Bai Airport (HAN) - Hanoi',
      'Da Nang Airport (DAD)',
      'Cam Ranh Airport (CXR) - Nha Trang',
      'Phu Quoc Airport (PQC)'
    ],
    requirements: {
      passportValidity: '6 months',
      photos: 2,
      returnTicket: true,
      hotelBooking: false,
      acceptedPaymentMethods: ['cash USD']
    },
    notes: 'Requires pre-approval letter from travel agency. E-visa ($25) now available for 90 days. 24 countries get visa-free 15-45 days.',
    lastUpdated: '2025-01-09'
  },

  'Myanmar': {
    country: 'Myanmar',
    available: true,
    eligiblePassports: ['China', 'India', 'All nationalities via eVisa'],
    cost: { amount: 50, currency: 'USD' },
    maxStayDays: 28,
    airports: [
      'Yangon International Airport (RGN)',
      'Mandalay International Airport (MDL)',
      'Nay Pyi Taw Airport (NYT)'
    ],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: true,
      acceptedPaymentMethods: ['cash USD', 'card']
    },
    notes: 'eVisa strongly recommended ($50). Check current situation before travel.',
    lastUpdated: '2025-01-09'
  },

  // ============= SOUTH ASIA =============
  'Maldives': {
    country: 'Maldives',
    available: true,
    eligiblePassports: ['All nationalities'],
    cost: { amount: 0, currency: 'USD' },
    maxStayDays: 30,
    airports: ['Velana International Airport (MLE)'],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: true,
      minimumFunds: 'Proof of sufficient funds ($100-$150 per day)',
      acceptedPaymentMethods: ['Free on arrival']
    },
    notes: 'Free 30-day tourist visa on arrival for all nationalities. Can be extended up to 90 days.',
    lastUpdated: '2025-01-09'
  },

  'Nepal': {
    country: 'Nepal',
    available: true,
    eligiblePassports: ['All nationalities except Nigeria and few restricted countries'],
    cost: { amount: 30, currency: 'USD' }, // 15 days
    maxStayDays: 15,
    airports: ['Tribhuvan International Airport (KTM) - Kathmandu'],
    landBorders: [
      'Birgunj', 'Sunauli', 'Kakarbhitta', 'Mahendranagar',
      'Rasuwagadhi (Tibet border)', 'Tatopani (Tibet border)'
    ],
    requirements: {
      passportValidity: '6 months',
      photos: 1,
      returnTicket: false,
      hotelBooking: false,
      acceptedPaymentMethods: ['cash USD', 'cash EUR', 'cash GBP', 'card']
    },
    notes: 'Multiple entry available. 15 days ($30), 30 days ($50), 90 days ($125). SAARC nationals free.',
    lastUpdated: '2025-01-09'
  },

  'Sri Lanka': {
    country: 'Sri Lanka',
    available: true,
    eligiblePassports: ['Singapore', 'Maldives', 'Seychelles (free)', 'Most others need ETA'],
    cost: { amount: 50, currency: 'USD' }, // ETA fee for most
    maxStayDays: 30,
    airports: ['Bandaranaike International Airport (CMB)'],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: true,
      acceptedPaymentMethods: ['card', 'cash USD']
    },
    notes: 'ETA (Electronic Travel Authorization) required for most. Free for nationals of 50+ countries as of 2024. Apply at www.srilankaevisa.lk',
    lastUpdated: '2025-01-09'
  },

  'Bangladesh': {
    country: 'Bangladesh',
    available: true,
    eligiblePassports: ['Business/Investment visitors from select countries'],
    cost: { amount: 51, currency: 'USD' },
    maxStayDays: 30,
    airports: ['Hazrat Shahjalal Airport (DAC) - Dhaka'],
    requirements: {
      passportValidity: '6 months',
      photos: 2,
      returnTicket: true,
      hotelBooking: true,
      acceptedPaymentMethods: ['cash USD']
    },
    notes: 'VOA mainly for business purposes. Tourist visa recommended to obtain in advance or via e-visa.',
    lastUpdated: '2025-01-09'
  },

  // ============= MIDDLE EAST =============
  'Jordan': {
    country: 'Jordan',
    available: true,
    eligiblePassports: ['All nationalities'],
    cost: { amount: 40, currency: 'JOD' }, // ~$56 USD
    maxStayDays: 30,
    airports: ['Queen Alia International Airport (AMM)'],
    landBorders: ['Wadi Araba (Aqaba)', 'Sheikh Hussein Bridge'],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: false,
      hotelBooking: false,
      acceptedPaymentMethods: ['cash JOD', 'cash USD', 'card']
    },
    notes: 'FREE if entering via Aqaba and staying 2+ nights. Jordan Pass ($70-80) includes visa + attractions. Highly recommended.',
    lastUpdated: '2025-01-09'
  },

  'Bahrain': {
    country: 'Bahrain',
    available: true,
    eligiblePassports: [
      'GCC nationals (free)', 'Australia', 'Austria', 'Belgium', 'Canada',
      'Denmark', 'Finland', 'France', 'Germany', 'Greece', 'Hong Kong',
      'Iceland', 'Ireland', 'Italy', 'Japan', 'Luxembourg', 'Malaysia',
      'Netherlands', 'New Zealand', 'Norway', 'Poland', 'Portugal',
      'Singapore', 'South Korea', 'Spain', 'Sweden', 'Switzerland',
      'Turkey', 'United Kingdom', 'United States'
    ],
    cost: { amount: 25, currency: 'BHD' }, // ~$66 USD for 2 weeks
    maxStayDays: 14,
    airports: ['Bahrain International Airport (BAH)'],
    landBorders: ['King Fahd Causeway (from Saudi Arabia)'],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: true,
      acceptedPaymentMethods: ['card']
    },
    notes: 'E-visa also available. Multiple entry 3-month visa costs 45 BHD.',
    lastUpdated: '2025-01-09'
  },

  'Oman': {
    country: 'Oman',
    available: true,
    eligiblePassports: [
      'All GCC nationals (free)', 'USA', 'UK', 'Canada', 'Australia', 
      'New Zealand', 'EU countries', 'Japan', 'South Korea', 'Singapore',
      'Hong Kong', 'Malaysia', 'Brunei'
    ],
    cost: { amount: 20, currency: 'OMR' }, // ~$52 USD
    maxStayDays: 14,
    airports: ['Muscat International Airport (MCT)', 'Salalah Airport (SLL)'],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: true,
      acceptedPaymentMethods: ['card']
    },
    notes: 'E-visa available for 10-30 days. GCC residents can get VOA regardless of nationality.',
    lastUpdated: '2025-01-09'
  },

  'Qatar': {
    country: 'Qatar',
    available: true,
    eligiblePassports: [
      'All GCC nationals', '80+ countries including USA, UK, EU, Australia, Japan, South Korea, Canada, New Zealand'
    ],
    cost: { amount: 0, currency: 'QAR' },
    maxStayDays: 30,
    airports: ['Hamad International Airport (DOH)'],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: true,
      acceptedPaymentMethods: ['Free on arrival']
    },
    notes: 'Free visa waiver for 80+ countries. Can be extended for another 30 days.',
    lastUpdated: '2025-01-09'
  },

  'UAE': {
    country: 'United Arab Emirates',
    available: true,
    eligiblePassports: [
      'GCC nationals (free)', 'USA', 'UK', 'EU countries', 'Australia',
      'New Zealand', 'Japan', 'South Korea', 'Singapore', 'Brunei',
      'Hong Kong', 'Malaysia'
    ],
    cost: { amount: 0, currency: 'AED' },
    maxStayDays: 30,
    airports: [
      'Dubai International Airport (DXB)',
      'Abu Dhabi Airport (AUH)',
      'Sharjah Airport (SHJ)'
    ],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: false,
      acceptedPaymentMethods: ['Free on arrival']
    },
    notes: 'Free 30-day visa for 60+ nationalities. Indians with valid US/UK/EU visa get 14-day free entry.',
    lastUpdated: '2025-01-09'
  },

  'Saudi Arabia': {
    country: 'Saudi Arabia',
    available: true,
    eligiblePassports: [
      'USA', 'UK', 'EU countries', 'Australia', 'New Zealand', 'Japan',
      'South Korea', 'Singapore', 'Malaysia', 'Brunei', 'Kazakhstan'
    ],
    cost: { amount: 480, currency: 'SAR' }, // ~$128 USD including insurance
    maxStayDays: 90,
    airports: [
      'King Abdulaziz Airport (JED) - Jeddah',
      'King Khalid Airport (RUH) - Riyadh',
      'King Fahd Airport (DMM) - Dammam'
    ],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: false,
      hotelBooking: false,
      acceptedPaymentMethods: ['card']
    },
    notes: 'E-visa available for tourism (1 year multiple entry, 90 days per visit). Women can travel solo.',
    lastUpdated: '2025-01-09'
  },

  'Kuwait': {
    country: 'Kuwait',
    available: true,
    eligiblePassports: [
      'GCC nationals', 'USA', 'UK', 'EU countries', 'Australia', 'Canada',
      'Japan', 'South Korea', 'Singapore', 'Malaysia'
    ],
    cost: { amount: 3, currency: 'KWD' }, // ~$10 USD
    maxStayDays: 90,
    airports: ['Kuwait International Airport (KWI)'],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: true,
      acceptedPaymentMethods: ['cash KWD', 'card']
    },
    notes: 'E-visa available at evisa.gov.kw. Business visitors may get 3-month visa.',
    lastUpdated: '2025-01-09'
  },

  // ============= EAST ASIA =============
  'Macau': {
    country: 'Macau',
    available: true,
    eligiblePassports: [
      'Most nationalities including USA, EU, Australia, New Zealand, Canada, Japan, South Korea, UK'
    ],
    cost: { amount: 0, currency: 'MOP' },
    maxStayDays: 30,
    airports: ['Macau International Airport (MFM)'],
    requirements: {
      passportValidity: '30 days beyond stay',
      photos: 0,
      returnTicket: false,
      hotelBooking: false,
      acceptedPaymentMethods: ['Free on arrival']
    },
    notes: 'Free visa-free entry for most nationalities. Duration varies: UK 180 days, EU/US 90 days, China 7 days.',
    lastUpdated: '2025-01-09'
  },

  'Hong Kong': {
    country: 'Hong Kong',
    available: true,
    eligiblePassports: [
      'Most nationalities including USA, UK, EU, Australia, Canada, Japan, South Korea'
    ],
    cost: { amount: 0, currency: 'HKD' },
    maxStayDays: 90,
    airports: ['Hong Kong International Airport (HKG)'],
    requirements: {
      passportValidity: '1 month beyond stay',
      photos: 0,
      returnTicket: true,
      hotelBooking: false,
      acceptedPaymentMethods: ['Free on arrival']
    },
    notes: 'Visa-free for most nationalities: UK 180 days, US/EU/Australia 90 days. Separate from mainland China visa.',
    lastUpdated: '2025-01-09'
  },

  // ============= CENTRAL ASIA =============
  'Kyrgyzstan': {
    country: 'Kyrgyzstan',
    available: true,
    eligiblePassports: [
      '64 countries visa-free including USA, EU, UK, Canada, Australia, Japan, South Korea, Turkey'
    ],
    cost: { amount: 0, currency: 'USD' },
    maxStayDays: 60,
    airports: ['Manas International Airport (FRU) - Bishkek'],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: false,
      hotelBooking: false,
      acceptedPaymentMethods: ['Free on arrival']
    },
    notes: 'Visa-free for 60 days for 64 countries. E-visa available for others at evisa.e-gov.kg',
    lastUpdated: '2025-01-09'
  },

  'Tajikistan': {
    country: 'Tajikistan',
    available: true,
    eligiblePassports: ['Most nationalities via e-Visa'],
    cost: { amount: 50, currency: 'USD' },
    maxStayDays: 45,
    airports: ['Dushanbe International Airport (DYU)'],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: false,
      hotelBooking: false,
      acceptedPaymentMethods: ['cash USD', 'card']
    },
    notes: 'E-visa strongly recommended at evisa.tj. GBAO permit ($20) required for Pamir Highway.',
    lastUpdated: '2025-01-09'
  },

  'Uzbekistan': {
    country: 'Uzbekistan',
    available: true,
    eligiblePassports: [
      '90+ countries visa-free including USA, EU, UK, Canada, Australia, Japan, South Korea, Israel'
    ],
    cost: { amount: 0, currency: 'USD' },
    maxStayDays: 30,
    airports: [
      'Islam Karimov Airport (TAS) - Tashkent',
      'Samarkand Airport (SKD)',
      'Bukhara Airport (BHK)'
    ],
    requirements: {
      passportValidity: '3 months',
      photos: 0,
      returnTicket: false,
      hotelBooking: false,
      acceptedPaymentMethods: ['Free on arrival']
    },
    notes: 'Visa-free for 90+ countries up to 30 days. E-visa available for other nationalities.',
    lastUpdated: '2025-01-09'
  },

  'Kazakhstan': {
    country: 'Kazakhstan',
    available: true,
    eligiblePassports: [
      '74 countries visa-free including USA, EU, UK, Canada, Australia, Japan, UAE, South Korea'
    ],
    cost: { amount: 0, currency: 'USD' },
    maxStayDays: 30,
    airports: [
      'Nursultan Nazarbayev Airport (NQZ) - Astana',
      'Almaty Airport (ALA)'
    ],
    requirements: {
      passportValidity: '3 months',
      photos: 0,
      returnTicket: false,
      hotelBooking: false,
      acceptedPaymentMethods: ['Free on arrival']
    },
    notes: 'Visa-free for 74 countries up to 30 days. E-visa available for others at vmp.gov.kz',
    lastUpdated: '2025-01-09'
  },

  // ============= AFRICA =============
  'Ethiopia': {
    country: 'Ethiopia',
    available: true,
    eligiblePassports: ['All nationalities via eVisa'],
    cost: { amount: 82, currency: 'USD' }, // 30-day single entry
    maxStayDays: 30,
    airports: ['Bole International Airport (ADD) - Addis Ababa'],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: true,
      acceptedPaymentMethods: ['card']
    },
    notes: 'E-visa required at evisa.gov.et. 90-day visa costs $102. VOA available for limited nationalities.',
    lastUpdated: '2025-01-09'
  },

  'Kenya': {
    country: 'Kenya',
    available: true,
    eligiblePassports: ['All nationalities via eTA'],
    cost: { amount: 30, currency: 'USD' },
    maxStayDays: 90,
    airports: [
      'Jomo Kenyatta Airport (NBO) - Nairobi',
      'Moi International Airport (MBA) - Mombasa'
    ],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: true,
      acceptedPaymentMethods: ['card']
    },
    notes: 'Electronic Travel Authorization (eTA) required since 2024 at etakenya.go.ke. East Africa Tourist Visa covers Kenya, Uganda, Rwanda.',
    lastUpdated: '2025-01-09'
  },

  'Tanzania': {
    country: 'Tanzania',
    available: true,
    eligiblePassports: ['All nationalities'],
    cost: { amount: 50, currency: 'USD' },
    maxStayDays: 90,
    airports: [
      'Julius Nyerere Airport (DAR) - Dar es Salaam',
      'Kilimanjaro Airport (JRO)',
      'Zanzibar Airport (ZNZ)'
    ],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: true,
      acceptedPaymentMethods: ['cash USD', 'card']
    },
    notes: 'E-visa recommended at visa.immigration.go.tz. US citizens pay $100. Yellow fever certificate required.',
    lastUpdated: '2025-01-09'
  },

  'Rwanda': {
    country: 'Rwanda',
    available: true,
    eligiblePassports: ['All nationalities'],
    cost: { amount: 50, currency: 'USD' },
    maxStayDays: 30,
    airports: ['Kigali International Airport (KGL)'],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: true,
      acceptedPaymentMethods: ['card']
    },
    notes: 'Visa on arrival for all African Union members. E-visa recommended at irembo.gov.rw. East Africa Tourist Visa available.',
    lastUpdated: '2025-01-09'
  },

  'Uganda': {
    country: 'Uganda',
    available: true,
    eligiblePassports: ['All nationalities'],
    cost: { amount: 50, currency: 'USD' },
    maxStayDays: 90,
    airports: ['Entebbe International Airport (EBB)'],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: true,
      acceptedPaymentMethods: ['card']
    },
    notes: 'E-visa required at visas.immigration.go.ug. East Africa Tourist Visa ($100) covers Uganda, Kenya, Rwanda.',
    lastUpdated: '2025-01-09'
  },

  'Zambia': {
    country: 'Zambia',
    available: true,
    eligiblePassports: ['Most nationalities'],
    cost: { amount: 50, currency: 'USD' },
    maxStayDays: 90,
    airports: [
      'Kenneth Kaunda Airport (LUN) - Lusaka',
      'Harry Mwanga Nkumbula Airport (LVI) - Livingstone'
    ],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: false,
      acceptedPaymentMethods: ['cash USD', 'card']
    },
    notes: 'KAZA UniVisa ($50) covers Zambia and Zimbabwe. E-visa at zambiaimmigration.gov.zm',
    lastUpdated: '2025-01-09'
  },

  'Zimbabwe': {
    country: 'Zimbabwe',
    available: true,
    eligiblePassports: ['Most nationalities'],
    cost: { amount: 30, currency: 'USD' }, // Single entry, double $45
    maxStayDays: 90,
    airports: [
      'Robert Mugabe Airport (HRE) - Harare',
      'Victoria Falls Airport (VFA)',
      'Joshua Mqabuko Airport (BUQ) - Bulawayo'
    ],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: false,
      acceptedPaymentMethods: ['cash USD', 'card']
    },
    notes: 'KAZA UniVisa ($50) covers Zimbabwe and Zambia. UK/US/Canada citizens pay $75 single entry.',
    lastUpdated: '2025-01-09'
  },

  'Egypt': {
    country: 'Egypt',
    available: true,
    eligiblePassports: [
      'USA', 'EU countries', 'UK', 'Australia', 'Canada', 'Japan', 
      'South Korea', 'Malaysia', 'Russia', 'Ukraine'
    ],
    cost: { amount: 25, currency: 'USD' },
    maxStayDays: 30,
    airports: [
      'Cairo International Airport (CAI)',
      'Hurghada Airport (HRG)',
      'Sharm El Sheikh Airport (SSH)',
      'Luxor Airport (LXR)'
    ],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: true,
      acceptedPaymentMethods: ['cash USD', 'cash EUR', 'card']
    },
    notes: 'E-visa available at visa2egypt.gov.eg. Sinai-only permit is free for 14 days.',
    lastUpdated: '2025-01-09'
  },

  'Morocco': {
    country: 'Morocco',
    available: true,
    eligiblePassports: [
      '69 countries visa-free including USA, UK, EU, Canada, Australia, Japan, South Korea'
    ],
    cost: { amount: 0, currency: 'MAD' },
    maxStayDays: 90,
    airports: [
      'Mohammed V Airport (CMN) - Casablanca',
      'Marrakech Airport (RAK)',
      'Fes Airport (FEZ)'
    ],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: false,
      acceptedPaymentMethods: ['Free on arrival']
    },
    notes: 'Visa-free for 69 countries up to 90 days. Extension possible at local police station.',
    lastUpdated: '2025-01-09'
  },

  'South Africa': {
    country: 'South Africa',
    available: true,
    eligiblePassports: [
      '50+ countries visa-free including USA, UK, EU, Australia, Canada, Japan, Brazil'
    ],
    cost: { amount: 0, currency: 'ZAR' },
    maxStayDays: 90,
    airports: [
      'O.R. Tambo Airport (JNB) - Johannesburg',
      'Cape Town Airport (CPT)',
      'King Shaka Airport (DUR) - Durban'
    ],
    requirements: {
      passportValidity: '30 days beyond departure + 2 blank pages',
      photos: 0,
      returnTicket: true,
      hotelBooking: false,
      acceptedPaymentMethods: ['Free on arrival']
    },
    notes: 'Visa-free for 50+ countries. Yellow fever certificate required if arriving from endemic area.',
    lastUpdated: '2025-01-09'
  },

  'Mauritius': {
    country: 'Mauritius',
    available: true,
    eligiblePassports: ['All nationalities'],
    cost: { amount: 0, currency: 'MUR' },
    maxStayDays: 60,
    airports: ['Sir Seewoosagur Ramgoolam Airport (MRU)'],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: true,
      minimumFunds: 'Proof of $100/day',
      acceptedPaymentMethods: ['Free on arrival']
    },
    notes: 'Free visa on arrival for all nationalities up to 60 days. Extendable to 6 months.',
    lastUpdated: '2025-01-09'
  },

  'Seychelles': {
    country: 'Seychelles',
    available: true,
    eligiblePassports: ['All nationalities'],
    cost: { amount: 0, currency: 'SCR' },
    maxStayDays: 90,
    airports: ['Seychelles International Airport (SEZ)'],
    requirements: {
      passportValidity: 'Valid for duration of stay',
      photos: 0,
      returnTicket: true,
      hotelBooking: true,
      minimumFunds: 'Proof of $150/day',
      acceptedPaymentMethods: ['Free on arrival']
    },
    notes: 'Free tourist permit for all nationalities. Extendable up to 12 months.',
    lastUpdated: '2025-01-09'
  },

  // ============= OCEANIA =============
  'Fiji': {
    country: 'Fiji',
    available: true,
    eligiblePassports: ['Over 100 countries visa-free including USA, UK, EU, Australia, NZ'],
    cost: { amount: 0, currency: 'FJD' },
    maxStayDays: 120,
    airports: ['Nadi International Airport (NAN)'],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: false,
      acceptedPaymentMethods: ['Free on arrival']
    },
    notes: 'Free entry for 4 months for 100+ countries. Extendable to 6 months.',
    lastUpdated: '2025-01-09'
  },

  'Samoa': {
    country: 'Samoa',
    available: true,
    eligiblePassports: ['All nationalities'],
    cost: { amount: 0, currency: 'WST' },
    maxStayDays: 60,
    airports: ['Faleolo International Airport (APW)'],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: true,
      acceptedPaymentMethods: ['Free on arrival']
    },
    notes: 'Free entry permit for 60 days. Extension available at Immigration Office.',
    lastUpdated: '2025-01-09'
  },

  'Vanuatu': {
    country: 'Vanuatu',
    available: true,
    eligiblePassports: ['All nationalities'],
    cost: { amount: 0, currency: 'VUV' },
    maxStayDays: 30,
    airports: ['Port Vila Bauerfield Airport (VLI)'],
    requirements: {
      passportValidity: '6 months',
      photos: 0,
      returnTicket: true,
      hotelBooking: true,
      acceptedPaymentMethods: ['Free on arrival']
    },
    notes: 'Free visa on arrival for 30 days. Extendable up to 120 days.',
    lastUpdated: '2025-01-09'
  },

  // ============= AMERICAS =============
  'Bolivia': {
    country: 'Bolivia',
    available: true,
    eligiblePassports: ['USA (reciprocal fee)', 'Most South American countries free'],
    cost: { amount: 160, currency: 'USD' }, // US citizens
    maxStayDays: 90,
    airports: ['El Alto Airport (LPB) - La Paz', 'Viru Viru Airport (VVI) - Santa Cruz'],
    requirements: {
      passportValidity: '6 months',
      photos: 1,
      returnTicket: true,
      hotelBooking: true,
      acceptedPaymentMethods: ['cash USD']
    },
    notes: 'US citizens pay $160 for 10-year multiple entry visa. UK/EU/Canada visa-free up to 90 days.',
    lastUpdated: '2025-01-09'
  },

  'Turkey': {
    country: 'Turkey',
    available: true,
    eligiblePassports: [
      'USA', 'UK', 'Australia', 'Canada', 'China', 'India', 'Jamaica', 'Mexico',
      'South Africa', 'Armenia', 'Portugal', 'Spain'
    ],
    cost: { amount: 50, currency: 'USD' },
    maxStayDays: 30,
    airports: [
      'Istanbul Airport (IST)',
      'Sabiha Gokcen Airport (SAW)',
      'Antalya Airport (AYT)',
      'Izmir Airport (ADB)'
    ],
    requirements: {
      passportValidity: '60 days beyond visa expiry',
      photos: 0,
      returnTicket: false,
      hotelBooking: false,
      acceptedPaymentMethods: ['card']
    },
    notes: 'E-visa required at evisa.gov.tr. Many EU countries visa-free for 90 days. UK citizens pay $50.',
    lastUpdated: '2025-01-09'
  }
};

/**
 * Get VOA information for a specific country
 */
export function getVisaOnArrivalInfo(country: string): VisaOnArrivalInfo | null {
  const normalized = country.trim();
  // Try exact match
  if (VISA_ON_ARRIVAL_DATA[normalized]) {
    return VISA_ON_ARRIVAL_DATA[normalized];
  }
  // Try case-insensitive match
  const found = Object.keys(VISA_ON_ARRIVAL_DATA).find(
    key => key.toLowerCase() === normalized.toLowerCase()
  );
  return found ? VISA_ON_ARRIVAL_DATA[found] : null;
}

/**
 * Check if a passport holder is eligible for VOA in a destination
 */
export function isEligibleForVOA(destination: string, passportCountry: string): boolean {
  const voaInfo = getVisaOnArrivalInfo(destination);
  if (!voaInfo || !voaInfo.available) return false;
  
  // Check if passport country is in eligible list
  const eligibleList = voaInfo.eligiblePassports?.join(' ').toLowerCase() || '';
  if (eligibleList.includes('all nationalities')) return true;
  
  return voaInfo.eligiblePassports?.some(
    p => p.toLowerCase().includes(passportCountry.toLowerCase())
  ) || false;
}

/**
 * Get all countries that offer VOA
 */
export function getCountriesWithVOA(): string[] {
  return Object.keys(VISA_ON_ARRIVAL_DATA);
}

/**
 * Get VOA cost formatted as string
 */
export function getFormattedVOACost(country: string): string {
  const info = getVisaOnArrivalInfo(country);
  if (!info || !info.cost) return 'Unknown';
  if (info.cost.amount === 0) return 'Free';
  return `${info.cost.currency} ${info.cost.amount.toLocaleString()}`;
}